<template>
    <KeywordDefinition
        v-if="
            showKeywordDefinition
                && visibleKeywordDefinition
                && question
                && questionContainer
                && keywordDefinitionParams
        "
        :keywordDOMElement="keywordDefinitionParams.keywordDOMElement"
        :keywordClickLocation="keywordDefinitionParams.keywordClickLocation"
        :keyword="visibleKeywordDefinition.keyword"
        :definition="visibleKeywordDefinition.definition"
        :questionContainer="questionContainer"
        :serial="question.serial"
        :keywordLocation="visibleKeywordDefinition.location"
        @close="showKeywordDefinition = false"
    />
    <QuizContainer
        ref="quizContainer"
        class="shared-qotd"
        :class="{ 'shared-qotd--show-banner': !!answer }"
    >
        <template #header>
            <div class="shared-qotd__header">
                <div class="shared-qotd__header-top" :class="{ 'shared-qotd__header-top--show-banner': !!answer }">
                    <div class="shared-qotd__logo">
                        <img src="@/assets/logos/logo-white.svg" alt="Pocket Prep Logo.">
                    </div>
                    <PocketButton
                        class="shared-qotd__register-btn"
                        type="secondary-yellow-dark"
                        @click="router.push({ name: 'register', query: { exam: examGuid } })"
                    >
                        Create an Account
                    </PocketButton>
                </div>
                <div v-if="!!answer" class="shared-qotd__header-banner">
                    <div class="shared-qotd__header-banner-questions">
                        <span>{{ examQuestionCount }}<Highlight /></span>
                        more questions
                    </div>
                    <div class="shared-qotd__header-banner-text">
                        In-depth explanations for every answer
                    </div>
                </div>
            </div>
        </template>
        <template #question>
            <Question
                v-if="question && examMetadata"
                :key="`${question.objectId}-${allowKeyboardShortcuts}-${JSON.stringify(answer)}`"
                class="shared-qotd__question"
                ref="qotdContainer"
                :container-el="quizContainerEl"
                :question="question"
                :quiz-mode="'qotd'"
                :global-metrics="!!answer && showGlobalMetrics ? globalMetrics : null"
                :show-next-question="false"
                :show-check-answer="true"
                :previous-choices="previousChoicesTyped"
                :previous-matrix-choices="previousMatrixChoicesTyped"
                :previous-build-list-choices="previousBuildListChoicesTyped"
                :previous-multi-part-multiple-choice-choices="previousChoicesTyped"
                :initial-show-answers="!!answer"
                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                :image-url-prefix="`${s3Url || ''}/${question.compositeKey.toUpperCase()}/`"
                :hide-references="examMetadata.hideReferences"
                :keyword-definitions="keywordDefinitions"
                @keywordClick="keywordClicked"
                @checkAnswer="submitQotD"
                @update:showExplanation="explanationViewToggled"
            >
                <template #context>
                    <div
                        class="shared-qotd__question-context"
                        :class="{ 'shared-qotd__question-context--show-banner': !!answer }"
                    >
                        <div class="shared-qotd__question-context-left">
                            <div class="shared-qotd__question-context-qotd">
                                Question of the Day
                            </div>
                            <div class="shared-qotd__question-context-exam">
                                {{ examMetadata.nativeAppName }}
                            </div>
                        </div>
                        <div class="shared-qotd__question-context-right">
                            <BundleIcon v-if="bundle" :bundle-id="bundle.objectId" />
                        </div>
                    </div>
                </template>
                <template #afterQuestion>
                    <div
                        v-if="!!answer"
                        :style="{ marginTop: questionBannerMarginTop }"
                        class="shared-qotd__footer-banner"
                    >
                        <div class="shared-qotd__footer-banner-questions">
                            <span>{{ examQuestionCount }}<Highlight /></span>
                            more questions
                        </div>
                        <div class="shared-qotd__footer-banner-text">
                            In-depth explanations for every answer
                        </div>
                        <PocketButton
                            class="shared-qotd__footer-banner-btn"
                            type="primary-yellow"
                            @click="router.push({ name: 'register', query: { exam: examGuid } })"
                        >
                            Create an Account
                        </PocketButton>
                        <div class="shared-qotd__footer-banner-blob">
                            <Blob />
                        </div>
                    </div>
                </template>
            </Question>
        </template>
        <template #footer>
            <div class="shared-qotd__footer">
                <div
                    class="shared-qotd__global-metrics-toggle"
                    :class="{
                        'shared-qotd__global-metrics-toggle--active': showGlobalMetrics,
                        'shared-qotd__global-metrics-toggle--disabled': !answer,
                        'shared-qotd__global-metrics-toggle--enabled': !!answer
                    }"
                >
                    <GlobalMetricsToggle
                        :show-global-metrics="showGlobalMetrics"
                        :disabled="!answer"
                        :tooltip-theme="
                            (
                                breakpoint === 'polar-bear'
                                || breakpoint === 'grizzly-bear'
                                || breakpoint === 'brown-bear'
                            )
                                ? 'rightalign' : undefined
                        "
                        @toggleGlobalMetrics="toggleGlobalMetrics"
                    />
                </div>
                <div class="shared-qotd__share">
                    <div class="shared-qotd__share-text">
                        Share with your people
                    </div>
                    <div
                        class="shared-qotd__share-link shared-qotd__share-link--copy"
                        tabindex="0"
                        @click="copyShareLink"
                        @keypress.enter="copyShareLink"
                        @mousedown.prevent
                    >
                        <Tooltip
                            v-if="showLinkCopiedTooltip"
                            class="shared-qotd__share-link-tooltip"
                            :styles="{
                                backgroundColor: brandColors.green,
                                color: brandColors.brandBlack,
                            }"
                        >
                            Link Copied!
                        </Tooltip>
                        <LinkIcon class="shared-qotd__share-link-icon--desktop" />
                        <Icon type="share" class="shared-qotd__share-link-icon--mobile" />
                    </div>
                    <ShareNetwork
                        v-if="qotdSharingInfo"
                        network="facebook"
                        :url="qotdSharingInfo.url"
                        :title="qotdSharingInfo.title"
                        :hashtags="qotdSharingInfo.hashtags"
                        class="shared-qotd__share-link"
                    >
                        <FacebookIcon />
                    </ShareNetwork>
                    <ShareNetwork
                        v-if="qotdSharingInfo"
                        network="twitter"
                        :url="qotdSharingInfo.url"
                        :title="qotdSharingInfo.title"
                        :hashtags="qotdSharingInfo.hashtags"
                        class="shared-qotd__share-link"
                    >
                        <TwitterIcon />
                    </ShareNetwork>
                </div>
                <KeyboardShortcutsButton
                    class="shared-qotd__keyboard-shortcuts-btn"
                    :tooltip-theme="
                        (breakpoint === 'polar-bear' || breakpoint === 'grizzly-bear' || breakpoint === 'brown-bear')
                            ? 'leftalign' : undefined
                    "
                >
                    <template
                        #keyboardShortcutsModal="{
                            showKeyboardShortcutsModal,
                            closeModalFunc
                        }"
                    >
                        <Portal to="modal">
                            <KeyboardShortcutsModal
                                v-if="showKeyboardShortcutsModal"
                                key="sharedQotdKeyboardShortcutsModal"
                                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                aria-live="polite"
                                @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                @close="closeModalFunc"
                            />
                        </Portal>
                    </template>
                </KeyboardShortcutsButton>
            </div>
        </template>
    </QuizContainer>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { bundleModule } from '@/store/bundle/module'
import { qotdModule } from '@/store/qotd/module'
import { userModule } from '@/store/user/module'
import type { Study } from '@pocketprep/types'
import type { ClientBundle, ClientExam } from '@/store/bundle/state'
import UIKit from '@pocketprep/ui-kit'
import FacebookIcon from '@/assets/shared-qotd/Facebook.vue'
import TwitterIcon from '@/assets/shared-qotd/Twitter.vue'
import LinkIcon from '@/assets/shared-qotd/Link.vue'
import { ShareNetwork } from 'vue-social-sharing'
import { copyText } from '@/utils'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import Blob from '@/assets/shared-qotd/Blob.vue'
import Highlight from '@/assets/shared-qotd/Highlight.vue'
import { screenModule } from '@/store/screen/module'
import { ResizeObserver as ResizeObserverPonyFill } from 'resize-observer'
import { analyticsModule } from '@/store/analytics/module'
import KeywordDefinition from '@/components/KeywordDefinitions/KeywordDefinition.vue'
import { keywordsModule } from '@/store/keywords/module'
import { examMetadataModule } from '@/store/examMetadata/module'

interface IBundleBrandInfo {
    appClipBundleId: string
    appStoreId: string
    appStoreURL: string
    color: string
    universalLinkName: string
    playStoreURL: string
}

// UIKit Question component ChoiceKey props
type TChoiceKey = `${'a' | 'd'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`
type TMatrixChoiceKey = `${'a' | 'd'}${1 | 2 | 3}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`
type TBuildListChoiceKey = `a${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`

const {
    KeyboardShortcutsButton,
    KeyboardShortcutsModal,
    Icon,
    Button: PocketButton,
    QuizContainer,
    Question,
    GlobalMetricsToggle,
    BundleIcon,
    Tooltip,
} = UIKit

const route = useRoute()
const router = useRouter()

const quizContainer = ref<InstanceType<typeof UIKit.QuizContainer> | null>(null)
const qotdContainer = ref<InstanceType<typeof UIKit.Question> | null>(null)

const allowKeyboardShortcuts = ref(false)
const isLoading = ref(true)
const question = ref<Study.Class.QuestionJSON | null>(null)
const globalMetrics = ref<Study.Class.GlobalQuestionMetricJSON | null>(null)
const showGlobalMetrics = ref(false)
const brandColors = BrandColors
const showLinkCopiedTooltip = ref(false)
const questionBannerMarginTop = ref('0')
const bundle = ref<Study.Class.BundleJSON | ClientBundle | null>(null)
const examMetadata = ref<Study.Class.ExamMetadataJSON | ClientExam | null>(null)
const resizeObserver = ref<ResizeObserver | null>(null)
const clientVersion = import.meta.env.VUE_APP_VERSION
const keywordDOMElement = ref<HTMLElement | null>(null)
const showKeywordDefinition = ref(false)
const visibleKeywordDefinition = ref<{
    keyword: string
    definition: string
    location: string
} | null>(null)
const keywordClickLocation = ref<{ x: number; y: number } | null>(null)

const keywordDefinitionParams = computed(() => {
    const el = keywordDOMElement.value
    const loc = keywordClickLocation.value

    if (!el || !loc) return null
    return {
        keywordDOMElement: el,
        keywordClickLocation: loc,
    }
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const breakpoint = computed(() => {
    return screenModule.getters.getBreakpoint()
})

const questionContainer = computed(() => {
    const refVal = qotdContainer.value
    return refVal && '$el' in refVal ? refVal.$el as HTMLElement : null
})

const quizContainerEl = computed(() => {
    const refVal = quizContainer.value
    return refVal && '$el' in refVal ? refVal.$el : null
})

const answer = computed(() => {
    return qotdModule.state.sharedQotDQuizDraft?.answers[0] || null
})

const examGuid = computed(() => {
    return route.query.examGuid as string
})

const userId = computed(() => {
    return route.query.userId as string
})

const version = computed(() => {
    return route.query.version as string
})

const majorVersion = computed(() => {
    const majorVersionString = version.value.split('.')[0] || '1'
    return majorVersionString + '.'
})

const examQuestionCount = computed(() => {
    return (examMetadata.value?.itemCount || 0) - (examMetadata.value?.archivedCount || 0) - 1
})

const previousChoices = computed(() => {
    return answer.value?.selectedChoices || []
})

// Question component expects literal union types; cast from string[] for type compatibility
const previousChoicesTyped = computed(() => {
    // non matrix and build list questions
    return previousChoices.value as TChoiceKey[]
})

const previousMatrixChoicesTyped = computed(() => {
    return previousChoices.value as TMatrixChoiceKey[]
})

const previousBuildListChoicesTyped = computed(() => {
    return previousChoices.value as TBuildListChoiceKey[]
})

const bundleBrandLib = computed(() => {
    return bundleModule.getters.getBundleBrandLib()
})

const keywordDefinitions = computed(() => {
    const questionSerial = question.value?.serial
    const questionKeywords = questionSerial && keywordsModule.getters.getKeywordDefinitions().get(questionSerial)

    if (
        !answer.value
        || !questionKeywords
    ) {
        return []
    }

    return Array.from(questionKeywords, ([ keyword, definition ]) => ({
        keyword,
        definition,
    }))
})

// We need to handle the case where we don't actually have a bundle defined for the exam
const bundleBrandInfo = computed((): IBundleBrandInfo => {
    const defaultBundleBrandInfo: IBundleBrandInfo = {
        appClipBundleId: 'PL8WGSD3BU.com.pocketprep.app.ems.Clip',
        appStoreId: '1503584128',
        appStoreURL: 'https://apple.co/36GCG4s',
        color: '#1F3545',
        playStoreURL: 'https://bit.ly/3dYDh2X',
        universalLinkName: 'web',
    }
    const id = bundle.value?.objectId
    if (id) {
        const bundleBrandLibId = bundleBrandLib.value[id]
        if (bundleBrandLibId) {
            return bundleBrandLibId
        }
    }
    return defaultBundleBrandInfo
})

const qotdSharingInfo = computed(() => {
    if (!bundle.value || !examMetadata.value) {
        return undefined
    }

    return qotdModule.getters.getQotDSharingInfo({
        bundleId: bundle.value.objectId,
        examGuid: examGuid.value,
        version: version.value,
        userId: userId.value || '',
        examName: examMetadata.value.nativeAppName,
    })
})

onMounted(async () => {
    if (!examGuid.value || !majorVersion.value) {
        router.push({ name: 'sign-in' }).catch(() => {
            // vue-router may report an error on additional redirects, but we're fine with them
        })
        return
    }

    if (userModule.state.user) {
        router.push({ name: 'qotd' })
        return
    }

    // update Google Analytics with event data
    /* istanbul ignore next */
    await analyticsModule.actions.googleAnalyticsTrack('shared_qotd_viewed', {
        'event_category': 'Share',
        examGuid: examGuid.value,
        userId: userId.value,
        version: version.value,
    })

    await examMetadataModule.actions.fetchExamMetadata()
    const examMetadataList = examMetadataModule.getters.getExamMetadata()
    const examMetadataForGuid = examMetadataList.find(exam =>
        exam.examGuid === examGuid.value && exam.version === version.value
    )
    const examMetadataId = examMetadataForGuid?.objectId

    const sharedQotDInfo = await qotdModule.actions.fetchSharedQotD({
        examGuid: examGuid.value,
        version: version.value,
        examMetadataId,
    })
    question.value = sharedQotDInfo.question

    await keywordsModule.actions.fetchKeywordDefinitions([ question.value.serial ], false, examGuid.value)

    // check for locally stored answer and clear if for a different question
    if (answer.value && answer.value.questionSerial !== question.value?.serial) {
        qotdModule.actions.clearSharedQotDDraft()
    }

    bundle.value = sharedQotDInfo.bundle || null
    examMetadata.value = sharedQotDInfo.examMetadata || null
    globalMetrics.value = sharedQotDInfo.globalMetric || null

    // Dynamically add a meta tag for Apple App Clips
    const id = bundleBrandInfo.value.appStoreId
    const clipId = bundleBrandInfo.value.appClipBundleId
    const content = `app-id=${id}, app-clip-bundle-id=${clipId}, app-argument=${qotdSharingInfo.value?.url}`
    const appClipMeta = document.createElement('meta')
    appClipMeta.setAttribute('name', 'apple-itunes-app')
    appClipMeta.setAttribute('content', content)
    document.getElementsByTagName('head')[0]?.appendChild(appClipMeta)

    nextTick(() => {
        const questionContainerEl = document.querySelector('.uikit-question__main') as HTMLElement
        resizeObserver.value = window.ResizeObserver
            ? new ResizeObserver(updateQuestionBannerMarginTop)
            : new ResizeObserverPonyFill(updateQuestionBannerMarginTop)
        resizeObserver.value.observe(questionContainerEl)
    })

    isLoading.value = false
})

onBeforeUnmount(() => {
    resizeObserver.value?.disconnect()
})

const updateQuestionBannerMarginTop = () => {
    const questionContainerEl = document.querySelector('.uikit-question__main') as HTMLElement
    const questionBanner = document.querySelector('.shared-qotd__footer-banner') as HTMLElement

    if (!answer.value || !questionContainerEl || !questionBanner) {
        return
    }

    const existingTopMargin = Number(questionBanner.style.marginTop?.split('px')[0]) || 0
    const topMargin = (
        questionContainerEl.getBoundingClientRect().height
        + questionContainerEl.getBoundingClientRect().top
    )
    - (questionBanner.getBoundingClientRect().top + questionBanner.getBoundingClientRect().height)
    + existingTopMargin

    if (topMargin === 0) {
        return
    }

    questionBannerMarginTop.value = topMargin > 0 ? `${topMargin}px` : '0'
}

const keywordClicked = (params?: {
    keyword: string
    location: string
    clickLocation: { x: number; y: number }
    target: HTMLElement
}) => {
    showKeywordDefinition.value = false
    if (!params?.keyword) {
        return
    }

    const questionKeywords = question.value
        && keywordsModule.getters.getKeywordDefinitions().get(question.value.serial)
    const definition = questionKeywords?.get(params.keyword.toLowerCase())

    if (!definition) {
        return
    }

    analyticsModule.actions.amplitudeTrack('Keyword Definition Viewed', {
        examGuid: examMetadata.value?.examGuid,
        definitionKeyword: params.keyword,
        serial: question.value?.serial,
        location: params.location,
        quizMode: -1,
        studyMode: 'Shared QotD',
    })

    visibleKeywordDefinition.value = {
        keyword: params.keyword,
        definition,
        location: params.location,
    }
    keywordDOMElement.value = params.target
    keywordClickLocation.value = params.clickLocation

    nextTick(() => {
        showKeywordDefinition.value = true
    })
}

const toggleGlobalMetrics = (newVal: boolean) => {
    showGlobalMetrics.value = newVal
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    allowKeyboardShortcuts.value = newVal
}

const copyShareLink = async () => {
    if (!qotdSharingInfo.value) {
        return
    }

    if (navigator.share as unknown && qotdSharingInfo.value && window.innerWidth < 768) {
        try {
            await navigator.share({
                title: qotdSharingInfo.value.title,
                url: qotdSharingInfo.value.url,
            })
        } catch (err) {
            // noop
        }
        return
    }

    copyText(qotdSharingInfo.value?.url)
    showLinkCopiedTooltip.value = true
    setTimeout(() => {
        showLinkCopiedTooltip.value = false
    }, 2000)

    analyticsModule.actions.amplitudeTrack('QotD Shared', {
        bundleId: bundle.value?.objectId,
        client: 'Web Study',
        clientAppId: 'study.pocketprep.com',
        clientVersion,
        examGuid: examMetadata.value?.examGuid,
        organizationId: userModule.getters.getCurrentOrganizationId() || undefined,
        userDomain: userModule.getters.getUserDomain() || undefined,
    })
}

const submitQotD = (answerParam: Study.Cloud.IQuizAnswer) => {
    qotdModule.actions.saveSharedQotDDraft({
        answers: [ answerParam ],
        correctCount: answerParam.isCorrect ? 1 : 0,
        durationSeconds: 0,
        examGuid: examGuid.value,
        incorrectCount: answerParam.isCorrect ? 0 : 1,
        mode: -1,
        platform: 'Web',
        startedAt: new Date(),
    })
}

const explanationViewToggled = (showExplanation: boolean) => {
    if (showExplanation) {
        analyticsModule.actions.amplitudeTrack(
            'Explanation Opened',
            {
                examGuid: examGuid.value,
                bundleId: bundle.value?.objectId,
                serial: question.value?.serial,
                quizMode: -1,
                isSharedQotd: true,
                quizPlatform: 'Web',
                isReviewMode: false,
            }
        )
    }
}

watch(answer, updateQuestionBannerMarginTop)
</script>

<style lang="scss" scoped>
.shared-qotd {
    position: absolute;
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    overflow: hidden;

    :deep(.uikit-quiz-container__main) {
        @include breakpoint(black-bear) {
            height: calc(100% - 108px) !important;
        }
    }

    :deep(.uikit-question__close-icon) {
        display: none;
    }

    :deep(.uikit-quiz-container__header) {
        @include breakpoint(brown-bear) {
            height: 62px !important;
        }

        @include breakpoint(black-bear) {
            height: 54px !important;
        }
    }

    &--show-banner {
        :deep(.uikit-quiz-container__header) {
            height: 145px;
        }

        :deep(.uikit-quiz-container__main) {
            height: calc(100% - 211px);
        }
    }

    &__header {
        padding: 16px 0;
        width: 100%;

        @include breakpoint(brown-bear) {
            padding: 9px 0 13px;
        }

        @include breakpoint(black-bear) {
            padding: 6px 10px 8px;
        }
    }

    &__header-top {
        width: 100%;
        display: flex;
        justify-content: space-between;

        &--show-banner {
            margin-bottom: 13px;
        }

        @include breakpoint(brown-bear) {
            margin-bottom: 0;
        }
    }

    &__logo {
        width: 134px;
        padding: 5px 0;

        img {
            display: block;
            width: 100%;
        }
    }

    &__footer-banner {
        width: calc(100% + 84px);
        height: 203px;
        background: $white;
        border-radius: 0 0 6px 6px;
        margin: 0 -42px;
        color: $brand-black;
        padding: 27px 81px;
        box-sizing: border-box;
        display: none;
        position: relative;
        z-index: 1;

        @include breakpoint(brown-bear) {
            display: block;
        }

        @include breakpoint(black-bear) {
            border-radius: 0 0 12px 12px;
            padding: 33px 26px;
            height: 274px;
            margin: 0 -17px;
            width: calc(100% + 34px);
        }
    }

    &__footer-banner-questions {
        font-size: 32px;
        line-height: 38px;
        letter-spacing: 0.32px;
        font-weight: 700;
        margin-bottom: 6px;

        span {
            z-index: 1;
            position: relative;

            svg {
                color: $beach-sand;
                position: absolute;
                left: -14px;
                top: 24px;
                width: calc(100% + 19px);
                z-index: -1;
                height: 18px;
            }
        }
    }

    &__footer-banner-blob {
        position: absolute;
        right: 0;
        bottom: 0;
        overflow: hidden;
        border-radius: 6px;
        z-index: -1;

        @include breakpoint(black-bear) {
            border-radius: 12px;
        }

        svg {
            display: block;
        }
    }

    &__footer-banner-text {
        font-size: 18px;
        line-height: 22px;
        margin-bottom: 23px;

        @include breakpoint(black-bear) {
            width: 225px;
            margin-bottom: 28px;
            line-height: 25px;
        }
    }

    &__footer-banner-btn {
        margin-left: -16px;

        @include breakpoint(black-bear) {
            margin-left: -8px;
        }
    }

    &__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;

        @include breakpoint(black-bear) {
            max-width: 375px;
            width: 100%;
            justify-content: space-around;
            margin: 0 auto;
        }
    }

    &__header-banner {
        background: #ffedcb;
        border-radius: 6px;
        height: 60px;
        width: 100%;
        display: flex;
        align-items: center;
        color: $brand-black;
        padding: 8px 30px;
        box-sizing: border-box;

        @include breakpoint(brown-bear) {
            display: none;
        }
    }

    &__header-banner-questions {
        font-size: 32px;
        letter-spacing: 0.32px;
        font-weight: 700;
        line-height: 44px;
        margin-right: 28px;

        span {
            z-index: 1;
            position: relative;

            svg {
                color: $banana-bread;
                position: absolute;
                left: -8px;
                top: 26px;
                width: calc(100% + 10px);
                z-index: -1;
            }
        }
    }

    &__header-banner-text {
        font-size: 18px;
        line-height: 25px;
    }

    &__question {
        position: relative;
        height: 100%;
    }

    &__global-metrics-toggle {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;

        &--active {
            background: $black-feather;
        }
    }

    &__keyboard-shortcuts-btn {
        position: relative;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__share {
        display: flex;
        align-items: center;
    }

    &__share-text {
        margin-right: 18px;

        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__share-link-tooltip {
        position: absolute;
        top: calc(-100% - 8px);
        left: 50%;
    }

    &__share-link {
        height: 28px;
        margin-right: 12px;
        color: $gray-divider;
        cursor: pointer;
        user-select: none;
        position: relative;
        outline: none;

        @include breakpoint(black-bear) {
            display: none;
        }

        &--copy {
            @include breakpoint(black-bear) {
                display: block;
                margin-right: 0;
            }
        }

        &:last-child {
            margin-right: 0;
        }

        @media (hover: hover) {
            &:hover,
            &:focus {
                color: $banana-bread;
            }
        }

        &:focus::before {
            content: "";
            position: absolute;
            width: 34px;
            height: 34px;
            border: 1px solid $banana-bread;
            border-radius: 34px;
            left: -4px;
            top: -4px;

            @include breakpoint(black-bear) {
                left: -9px;
            }
        }
    }

    &__share-link-icon--desktop {
        @include breakpoint(black-bear) {
            display: none;
        }
    }

    &__share-link-icon--mobile {
        display: none;

        @include breakpoint(black-bear) {
            display: block;
        }
    }

    &__question-context {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;
        padding: 56px 0 37px 16px;
        box-sizing: border-box;
        max-width: 492px;

        @include breakpoint(black-bear) {
            padding-bottom: 28px;
        }
    }

    &__question-context-qotd {
        font-size: 16px;
        font-weight: 600;
        line-height: 19px;
    }

    &__question-context-exam {
        font-size: 14px;
        line-height: 17px;
        opacity: 0.8;
    }

    &__question-context-right {
        height: 52px;
        width: 52px;
        margin-right: -7px;

        svg {
            width: 100%;
            height: 100%;
        }
    }
}
</style>
